"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const api_1 = tslib_1.__importDefault(require("./api"));
const cache_1 = tslib_1.__importDefault(require("./cache"));
const errorHandler = tslib_1.__importStar(require("./errorHandler"));
const sanitize_1 = require("./sanitize");
const state_1 = tslib_1.__importDefault(require("./state"));
class PageService {
    constructor(_a) {
        var { storageName } = _a, settings = tslib_1.__rest(_a, ["storageName"]);
        this.projectCacheKey = 'projectData';
        this.updateCredentials = (credentials) => {
            this.api.updateCredentials(credentials);
        };
        errorHandler.validateSettings(settings);
        this.api = new api_1.default(settings);
        this.state = new state_1.default({ storageName });
        this.cache = new cache_1.default({ storageName, storeName: 'page' });
    }
    getProjectDataFromCache() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const [dataFromCache, dataFromState] = yield Promise.all([
                this.cache.get(this.projectCacheKey),
                this.state.get(this.projectCacheKey),
            ]);
            return [dataFromCache, dataFromState !== null && dataFromState !== void 0 ? dataFromState : {}];
        });
    }
    getDesignData() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const [dataFromCache, dataFromState] = yield this.getProjectDataFromCache();
            if (dataFromCache)
                return Object.assign(Object.assign({}, dataFromCache), dataFromState);
            const data = yield this.api.getDesignData();
            this.api.setCompanyId(data);
            yield this.cache.set(this.projectCacheKey, data);
            return Object.assign(Object.assign({}, data), dataFromState);
        });
    }
    getTemplateData() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const [dataFromCache, dataFromState] = yield this.getProjectDataFromCache();
            if (dataFromCache)
                return Object.assign(Object.assign({}, dataFromCache), dataFromState);
            const data = yield this.api.getTemplateData();
            this.api.setCompanyId(data);
            yield this.cache.set(this.projectCacheKey, data);
            return Object.assign(Object.assign({}, data), dataFromState);
        });
    }
    saveDesignData(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.cache.delete(this.projectCacheKey);
            return yield this.api.saveDesignData(data);
        });
    }
    saveTemplateData(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.cache.delete(this.projectCacheKey);
            return yield this.api.saveTemplateData(data);
        });
    }
    saveProjectData(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.saveProjectData(data);
            yield this.cache.delete(this.projectCacheKey);
            return response;
        });
    }
    saveProjectState(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const state = yield this.state.get(this.projectCacheKey);
            const newState = Object.assign(Object.assign({}, state), data);
            return yield this.state.set(this.projectCacheKey, newState);
        });
    }
    getPage(id, options = {}) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!id)
                throw new Error('pageId is required');
            const [dataFromCache, dataFromState] = yield Promise.all([
                this.cache.get(id),
                yield this.state.get(id),
            ]);
            const contentFromCache = dataFromState !== null && dataFromState !== void 0 ? dataFromState : dataFromCache === null || dataFromCache === void 0 ? void 0 : dataFromCache.content;
            if (dataFromCache && contentFromCache && !options.disableCache) {
                return Object.assign(Object.assign({}, dataFromCache), { content: (0, sanitize_1.sanitizeContent)(contentFromCache) });
            }
            const data = yield this.api.getPage(id);
            return yield this.cache.set(id, data);
        });
    }
    getPages(pages, options = {}) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(pages) || pages.length < 1) {
                throw new Error('getPages expects an array of pages');
            }
            return yield Promise.all(pages.map((page) => this.getPage(page, options)));
        });
    }
    createNewPage(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.cache.delete(this.projectCacheKey);
            return yield this.api.createNewPage(data);
        });
    }
    duplicatePage(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.cache.delete(this.projectCacheKey);
            return yield this.api.duplicatePage(data);
        });
    }
    savePage(id, content, additionalData = {}) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!id)
                throw new Error('id is required');
            if (!content)
                throw new Error('content is required');
            yield this.api.savePage(id, content, additionalData);
            yield this.state.delete(id);
            const dataFromCache = yield this.cache.get(id);
            return yield this.cache.set(id, Object.assign(Object.assign({}, dataFromCache), { content }));
        });
    }
    savePageState(id, state) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!state)
                throw new Error('state is required');
            yield this.state.set(id, state);
        });
    }
    hasUpdates() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.state.hasUpdates();
        });
    }
    propagateStateChanges() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const modifications = yield this.state.getAll();
            const promises = modifications.map(({ id, data }) => {
                if (id === this.projectCacheKey) {
                    return this.saveProjectData(data);
                }
                return this.savePage(id, data);
            });
            const response = yield Promise.all(promises);
            yield this.state.clearAll();
            return response;
        });
    }
    deletePage(id) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!id)
                throw new Error('id is required');
            const [, , response] = yield Promise.all([
                this.cache.delete(id),
                this.cache.delete(this.projectCacheKey),
                this.api.deletePage(id),
                this.state.delete(id),
            ]);
            return response;
        });
    }
}
exports.default = PageService;
//# sourceMappingURL=service.js.map